import React, { useState, useCallback } from 'react';
import { Button, Card } from '@/components/ui';
import {
  PlatformStats,
  TenantGrowthChart,
  RevenueChart,
  TopTenants,
  PendingActions,
  SystemHealth,
  AuditLogPreview,
  UserDistribution,
} from '@/components/dashboard';
import { adminStatsService } from '@/services/adminStats.service';

type DateRangeOption = '7d' | '30d' | '90d' | '12m' | 'custom';

interface DateRange {
  startDate: string;
  endDate: string;
}

const getDateRange = (option: DateRangeOption): DateRange => {
  const endDate = new Date();
  const startDate = new Date();

  switch (option) {
    case '7d':
      startDate.setDate(endDate.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(endDate.getDate() - 30);
      break;
    case '90d':
      startDate.setDate(endDate.getDate() - 90);
      break;
    case '12m':
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
    default:
      startDate.setDate(endDate.getDate() - 30);
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

export const Dashboard: React.FC = () => {
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>('30d');
  const [isExporting, setIsExporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleExport = useCallback(async () => {
    try {
      setIsExporting(true);
      const dateRange = getDateRange(dateRangeOption);
      const blob = await adminStatsService.exportDashboardData(dateRange);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      // In production, show toast notification
      alert('Export functionality is not yet available. This is a stub implementation.');
    } finally {
      setIsExporting(false);
    }
  }, [dateRangeOption]);

  const dateRangeOptions: { value: DateRangeOption; label: string }[] = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '12m', label: 'Last 12 Months' },
  ];

  return (
    <div className="space-y-6" key={refreshKey}>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-secondary-500">
            Comprehensive overview of platform statistics, tenant performance, and system health
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Date Range Selector */}
          <div className="flex items-center space-x-1 bg-secondary-100 rounded-lg p-1">
            {dateRangeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setDateRangeOption(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  dateRangeOption === option.value
                    ? 'bg-white text-secondary-900 shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <Button variant="secondary" onClick={handleRefresh}>
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </Button>

            <Button
              variant="outline"
              onClick={handleExport}
              isLoading={isExporting}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Platform Stats Overview - Full Width */}
      <PlatformStats />

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Column - 2/3 width on XL */}
        <div className="xl:col-span-2 space-y-6">
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TenantGrowthChart />
            <RevenueChart />
          </div>

          {/* Top Tenants Table */}
          <TopTenants />

          {/* Audit Log Preview */}
          <AuditLogPreview />
        </div>

        {/* Right Sidebar - 1/3 width on XL */}
        <div className="space-y-6">
          {/* Pending Actions */}
          <PendingActions />

          {/* System Health */}
          <SystemHealth />

          {/* User Distribution */}
          <UserDistribution />
        </div>
      </div>

      {/* Quick Actions Footer */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-secondary-900">Quick Actions</h2>
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            <QuickActionButton
              to="/topup-requests"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              }
              label="Review Topups"
              color="warning"
            />
            <QuickActionButton
              to="/users"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
              }
              label="Manage Users"
              color="primary"
            />
            <QuickActionButton
              to="/tenants"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              }
              label="View Tenants"
              color="success"
            />
            <QuickActionButton
              to="/audit-logs"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
              }
              label="Audit Logs"
              color="info"
            />
            <QuickActionButton
              to="/settings"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              }
              label="Settings"
              color="secondary"
            />
            <QuickActionButton
              to="/reports"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              }
              label="Reports"
              color="purple"
            />
          </div>
        </Card.Body>
      </Card>

      {/* Footer Stats Summary */}
      <div className="bg-secondary-50 rounded-xl p-4 border border-secondary-200">
        <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-secondary-600">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Dashboard data auto-refreshes every 5 minutes</span>
          </div>
          <div className="flex items-center space-x-4">
            <span>Last updated: {new Date().toLocaleString()}</span>
            <span className="flex items-center space-x-1">
              <span className="w-2 h-2 bg-success-500 rounded-full animate-pulse" />
              <span>Live</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quick Action Button Component
interface QuickActionButtonProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  color: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary' | 'purple';
}

const colorClasses = {
  primary: 'bg-primary-50 text-primary-600 hover:bg-primary-100',
  success: 'bg-success-50 text-success-600 hover:bg-success-100',
  warning: 'bg-warning-50 text-warning-600 hover:bg-warning-100',
  danger: 'bg-danger-50 text-danger-600 hover:bg-danger-100',
  info: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
  secondary: 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200',
  purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100',
};

const QuickActionButton: React.FC<QuickActionButtonProps> = ({ to, icon, label, color }) => {
  return (
    <a
      href={to}
      className={`flex flex-col items-center justify-center p-4 rounded-xl transition-colors ${colorClasses[color]}`}
    >
      {icon}
      <span className="mt-2 text-xs font-medium text-center">{label}</span>
    </a>
  );
};

export default Dashboard;
